import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../shared/environments/environment.dev';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private keycloakInstance: Keycloak;
  private readonly authChannelName = 'kavach-auth-channel';
  private readonly logoutEventType = 'LOGOUT';
  private readonly logoutEventStorageKey = 'kavach-auth-logout-event';
  private logoutChannel?: BroadcastChannel;
  private isLogoutInProgress = false;

  constructor() {
    console.log("constrcutor called")
    this.keycloakInstance = new Keycloak({
      url: environment.KEYCLOAK_URLS.BASE_URL,
      realm: environment.REALM_NAME,
      clientId: environment.CLIENT_ID,
    });
    this.setupCrossTabLogoutListener();
  }

  init(): Promise<void> {
    return this.keycloakInstance
      .init({
        onLoad: 'login-required',
        checkLoginIframe: false
      })
      .then((authenticated) => {
        if (!authenticated) {
          localStorage.clear();
          return this.keycloakInstance.login({ redirectUri: environment.KEYCLOAK_URLS.REDIRECT_URL });
        }
        const isAccessDenied = window.location.href.includes('access-denied');
        if (isAccessDenied && !localStorage.getItem('userPermissions')) {
          window.location.href = environment.KEYCLOAK_URLS.REDIRECT_URL;
        }
        this.setupTokenMonitoring()

        return Promise.resolve();
      })
      .catch((error) => {
        console.error('Keycloak initialization failed', error);
        return Promise.reject(error);
      });
  }

  async logout() {
  const body = new URLSearchParams();
  body.append("client_id", environment.CLIENT_ID);
  body.append("id_token_hint", this.keycloakInstance.idToken!);
  body.append("post_logout_redirect_uri", environment.KEYCLOAK_URLS.LOGOUT_URL);
  try {
    await fetch(
      `${environment.KEYCLOAK_URLS.BASE_URL}/realms/${environment.REALM_NAME}/custom-logout/logout`,
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
        body: body.toString()
      }
    );
  } catch (error) {
    console.error('Logout API failed:', error);
  } finally {
    localStorage.clear();
    sessionStorage.clear();
    this.keycloakInstance.clearToken();
    window.location.replace('/landing');
  }
}

  clearToken(): void {
    if (this.keycloakInstance) {
      this.keycloakInstance.clearToken();
      console.log('Token cleared from Keycloak instance');
    } else {
      console.warn('Keycloak instance is not initialized');
    }
  }

  isLoggedIn(): boolean {
    return !!this.keycloakInstance && !!this.keycloakInstance.authenticated;
  }

  setupTokenMonitoring(): void {
    if (this.keycloakInstance) {
      this.keycloakInstance.onTokenExpired = () => {
        console.warn('Token expired, attempting to refresh');
        this.keycloakInstance
          .updateToken(30)
          .then((refreshed) => {
            if (!refreshed) {
              console.warn('Token not refreshed, logging out');
              this.logout();
            } else {
              console.log('Token refreshed successfully');
            }
          })
          .catch((error) => {
            console.error('Failed to refresh the token', error);
            this.logout();
          });
      };
    } else {
      console.warn('Keycloak instance is not initialized for token monitoring');
    }
  }

  getToken(): Promise<string> {
    if (!this.keycloakInstance) {
      return Promise.reject('Keycloak instance is not initialized');
    }

    return this.keycloakInstance
      .updateToken(30) 
      .then(() => {
        const token = this.keycloakInstance.token;
        if (!token) {
          return Promise.reject('Token is unavailable');
        }
        return token;
      })
      .catch((error) => {
        console.error('Failed to refresh the token', error);
        return Promise.reject('Failed to get a valid token');
      });
  }



  getKeycloakInstance(): Keycloak {
    return this.keycloakInstance;
  }

  private setupCrossTabLogoutListener(): void {
    if (!this.isBrowser()) {
      return;
    }

    if ('BroadcastChannel' in window) {
      this.logoutChannel = new BroadcastChannel(this.authChannelName);
      this.logoutChannel.onmessage = (event: MessageEvent) => {
        if (this.isLogoutEvent(event.data)) {
          this.logout();
        }
      };
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.logoutEventStorageKey && event.newValue) {
        this.logout();
      }
    });
  }

  private broadcastLogout(): void {
    const logoutEvent = {
      type: this.logoutEventType,
      timestamp: Date.now(),
    };

    this.logoutChannel?.postMessage(logoutEvent);
    localStorage.setItem(this.logoutEventStorageKey, JSON.stringify(logoutEvent));
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined' && typeof sessionStorage !== 'undefined';
  }

  private isLogoutEvent(eventData: unknown): eventData is { type: string; timestamp: number } {
    return !!eventData && typeof eventData === 'object' && (eventData as { type?: string }).type === this.logoutEventType;
  }
}
